import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronRight,
  MessageSquare,
  Sparkles,
  Copy,
  Search,
  Briefcase,
  Megaphone,
  Code,
  PenTool,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  BookOpen,
  Lightbulb,
  Star
} from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'what-are-prompts', title: 'What Are SweetPilot Prompts?' },
  { id: 'prompt-categories', title: 'Prompt Categories' },
  { id: 'how-to-use', title: 'How to Use Prompts' },
  { id: 'plus-bundle', title: 'SweetPilot Plus Bundle' },
  { id: 'best-practices', title: 'Best Practices' },
  { id: 'migration', title: 'Migration to AI Employees' },
  { id: 'faq', title: 'Frequently Asked Questions' },
];

const promptCategories = [
  {
    name: 'Business',
    icon: Briefcase,
    count: 450,
    color: 'blue',
    description: 'Strategic planning, business analysis, operations management'
  },
  {
    name: 'Marketing',
    icon: Megaphone,
    count: 680,
    color: 'purple',
    description: 'Campaign strategies, social media, email marketing, SEO'
  },
  {
    name: 'Sales',
    icon: TrendingUp,
    count: 320,
    color: 'green',
    description: 'Lead generation, pitch decks, sales scripts, negotiations'
  },
  {
    name: 'Copywriting',
    icon: PenTool,
    count: 520,
    color: 'orange',
    description: 'Ad copy, landing pages, email sequences, product descriptions'
  },
  {
    name: 'HR & Recruitment',
    icon: Users,
    count: 180,
    color: 'pink',
    description: 'Job descriptions, interview questions, employee handbooks'
  },
  {
    name: 'Web Development',
    icon: Code,
    count: 280,
    color: 'cyan',
    description: 'Code generation, debugging, documentation, architecture'
  },
];

const samplePrompts = [
  {
    title: 'Competitor Analysis Framework',
    category: 'Business',
    preview: 'Analyze [COMPETITOR] across these dimensions: market positioning, pricing strategy, product features, marketing channels, customer reviews...',
  },
  {
    title: 'Social Media Content Calendar',
    category: 'Marketing',
    preview: 'Create a 30-day social media content calendar for [BRAND] in the [INDUSTRY] niche, including post ideas, hashtags, and optimal posting times...',
  },
  {
    title: 'Cold Email Sequence',
    category: 'Sales',
    preview: 'Write a 5-email cold outreach sequence for [PRODUCT/SERVICE] targeting [IDEAL CUSTOMER]. Include subject lines, body copy, and CTAs...',
  },
  {
    title: 'Landing Page Copy',
    category: 'Copywriting',
    preview: 'Write high-converting landing page copy for [PRODUCT] that addresses [PAIN POINTS] and highlights [KEY BENEFITS]. Include headline variations...',
  },
];

const faqs = [
  {
    question: 'Are SweetPilot Prompts still available?',
    answer: 'Yes, SweetPilot Prompts remain accessible through the SweetPilot Plus bundle until February 1st, 2026. After this date, the prompts will be integrated into our AI Employees platform.',
  },
  {
    question: 'How many prompts are included in the Plus bundle?',
    answer: 'The Plus bundle includes access to over 10,000 prompts across 12 categories. While you see 2,432 prompt cards, each card contains up to 5 different prompt variations.',
  },
  {
    question: 'Can I customize the prompts?',
    answer: 'All prompts are fully customizable. Simply replace the placeholder variables with your specific information to generate tailored AI responses.',
  },
  {
    question: 'Will my purchased prompts still work after EOL?',
    answer: 'While legacy access will end, all prompt functionality has been enhanced and integrated into our AI Employees platform. We recommend migrating for the best experience.',
  },
];

const relatedArticles = [
  { title: 'SweetPilot Legacy', href: '/doc/article/sweetpilot-legacy' },
  { title: 'SweetPilot Bots', href: '/doc/article/sweetpilot-bots' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'Understanding AI Hallucinations', href: '/doc/article/ai-hallucinations' },
];

export default function SweetPilotPrompts() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; icon: string; text: string }> = {
      purple: {
        bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
        border: isDark ? 'border-purple-500/20' : 'border-purple-200',
        icon: isDark ? 'text-purple-400' : 'text-purple-600',
        text: isDark ? 'text-purple-300' : 'text-purple-700',
      },
      blue: {
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        border: isDark ? 'border-blue-500/20' : 'border-blue-200',
        icon: isDark ? 'text-blue-400' : 'text-blue-600',
        text: isDark ? 'text-blue-300' : 'text-blue-700',
      },
      green: {
        bg: isDark ? 'bg-green-500/10' : 'bg-green-50',
        border: isDark ? 'border-green-500/20' : 'border-green-200',
        icon: isDark ? 'text-green-400' : 'text-green-600',
        text: isDark ? 'text-green-300' : 'text-green-700',
      },
      orange: {
        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
        border: isDark ? 'border-orange-500/20' : 'border-orange-200',
        icon: isDark ? 'text-orange-400' : 'text-orange-600',
        text: isDark ? 'text-orange-300' : 'text-orange-700',
      },
      pink: {
        bg: isDark ? 'bg-pink-500/10' : 'bg-pink-50',
        border: isDark ? 'border-pink-500/20' : 'border-pink-200',
        icon: isDark ? 'text-pink-400' : 'text-pink-600',
        text: isDark ? 'text-pink-300' : 'text-pink-700',
      },
      cyan: {
        bg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
        border: isDark ? 'border-cyan-500/20' : 'border-cyan-200',
        icon: isDark ? 'text-cyan-400' : 'text-cyan-600',
        text: isDark ? 'text-cyan-300' : 'text-cyan-700',
      },
    };
    return colors[color] || colors.purple;
  };

  // SEO Schema markup for structured data
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SweetPilot Prompts - AI Prompts Library for Business',
    description: 'Complete guide to SweetPilot Prompts - over 10,000 AI prompts for business, marketing, sales, copywriting, and more.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: {
      '@type': 'Organization',
      name: 'SweetPilot',
      logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' }
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://sweetpilot.com/doc/article/sweetpilot-prompts'
    }
  };

  // FAQ Schema for rich snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SweetPilot Prompts - 10,000+ AI Prompts for Business | SweetPilot Help Center</title>
        <meta name="description" content="Discover SweetPilot Prompts - a library of 10,000+ AI prompts for business, marketing, sales, copywriting, HR, and web development. Master AI prompt engineering." />
        <meta name="keywords" content="SweetPilot Prompts, AI prompts, ChatGPT prompts, business prompts, marketing prompts, sales prompts, copywriting prompts, prompt engineering, AI writing prompts, prompt library, SweetPilot Plus" />
        <meta property="og:title" content="SweetPilot Prompts - 10,000+ AI Prompts for Business" />
        <meta property="og:description" content="Complete guide to SweetPilot Prompts - access over 10,000 AI prompts for business, marketing, sales, copywriting, and more." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/sweetpilot-prompts" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Prompts - AI Prompts Library" />
        <meta name="twitter:description" content="Access 10,000+ AI prompts for business, marketing, sales, and more. Master prompt engineering with SweetPilot." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/sweetpilot-prompts" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className={`flex items-center flex-wrap gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            <li>
              <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                All Collections
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li>
              <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                Getting started
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li>
              <Link to="/doc/collections/legacy-products" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                SweetPilot AI Legacy products
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" aria-hidden="true" /></li>
            <li className={isDark ? 'text-white' : 'text-slate-900'}>
              SweetPilot Prompts
            </li>
          </ol>
        </nav>

        <div className="flex gap-10">
          {/* Main Content */}
          <article className="flex-1 max-w-3xl">
            {/* Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                SweetPilot Prompts
              </h1>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Explore the prompt library that helped users get the most out of AI with over 10,000 expertly crafted prompts for business, marketing, sales, and more.
              </p>
              <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <MessageSquare className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <span>Written by </span>
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
                </div>
                <span>•</span>
                <span>Updated recently</span>
              </div>
            </motion.header>

            {/* EOL Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-5 rounded-xl border mb-8 ${
                isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
              }`}
              role="alert"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                <div>
                  <p className={`font-semibold mb-2 ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                    Legacy Product Notice
                  </p>
                  <p className={`text-sm ${isDark ? 'text-orange-200/80' : 'text-orange-700'}`}>
                    SweetPilot Prompts are part of our legacy product line (SweetPilot Plus). This prompt library will reach end-of-life on <strong>February 1st, 2026</strong>. The prompts functionality has been enhanced and integrated into our{' '}
                    <Link
                      to="/dashboard/employees"
                      className={`underline ${isDark ? 'text-orange-300 hover:text-orange-200' : 'text-orange-800 hover:text-orange-900'}`}
                    >
                      AI Employees
                    </Link>
                    {' '}platform.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Overview Section */}
            <motion.section
              id="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-10"
            >
              <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <strong>SweetPilot Prompts</strong> is a comprehensive library of AI prompts designed to help businesses leverage artificial intelligence effectively. With over 10,000 prompts across 12 categories, users can quickly generate high-quality content, strategies, and solutions without spending hours on prompt engineering.
              </p>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-center gap-3">
                  <Sparkles className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <strong className={isDark ? 'text-white' : 'text-slate-900'}>Pro Tip:</strong> Each prompt card contains up to 5 variations, giving you multiple approaches for the same task. Experiment with different variations to find what works best for your needs.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* What Are Prompts Section */}
            <motion.section
              id="what-are-prompts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What Are SweetPilot Prompts?
              </h2>

              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                SweetPilot Prompts are pre-written, expertly crafted instructions designed to help you get the best results from AI models like ChatGPT, Claude, and other large language models (LLMs). Instead of spending time figuring out how to phrase your requests, you can use our tested prompts and customize them for your specific needs.
              </p>

              <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Key Benefits of Using Prompts
              </h3>

              <ul className={`space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Save Time:</strong> Skip the trial and error of prompt engineering with ready-to-use templates</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Better Results:</strong> Our prompts are optimized for clarity, specificity, and output quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Learn Best Practices:</strong> Study the structure of effective prompts to improve your own skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span><strong>Consistency:</strong> Get reliable, consistent outputs for your business processes</span>
                </li>
              </ul>
            </motion.section>

            {/* Prompt Categories Section */}
            <motion.section
              id="prompt-categories"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Prompt Categories
              </h2>

              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                SweetPilot Prompts are organized into 12 categories covering essential business functions. Here are the main categories:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {promptCategories.map((category) => {
                  const colors = getColorClasses(category.color);
                  const Icon = category.icon;
                  return (
                    <div
                      key={category.name}
                      className={`p-4 rounded-xl border ${colors.bg} ${colors.border}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className={`w-6 h-6 flex-shrink-0 ${colors.icon}`} />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {category.name}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                              {category.count}+ prompts
                            </span>
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            {category.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                Additional categories include: Agency, Assistants, Productivity, Real Estate, Startups, and Support.
              </p>
            </motion.section>

            {/* How to Use Section */}
            <motion.section
              id="how-to-use"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                How to Use SweetPilot Prompts
              </h2>

              <ol className={`list-decimal list-inside space-y-3 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li>
                  Navigate to your{' '}
                  <Link
                    to="/dashboard"
                    className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    SweetPilot Dashboard
                  </Link>
                  {' '}and sign in.
                </li>
                <li>Select <strong>Prompts</strong> from the legacy section in the sidebar.</li>
                <li>Browse or search for prompts using categories or keywords.</li>
                <li>Click on a prompt card to view all variations and instructions.</li>
                <li>Copy the prompt and customize the <code className={`px-1 py-0.5 rounded ${isDark ? 'bg-dark-600' : 'bg-slate-100'}`}>[PLACEHOLDER]</code> variables.</li>
                <li>Paste into your preferred AI tool and generate your content.</li>
              </ol>

              <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Sample Prompts
              </h3>

              <div className="space-y-4">
                {samplePrompts.map((prompt, index) => (
                  <div
                    key={prompt.title}
                    className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {prompt.title}
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                          {prompt.category}
                        </span>
                      </div>
                      <button
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-gray-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
                        title="Copy prompt"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'} line-clamp-2`}>
                      {prompt.preview}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Plus Bundle Section */}
            <motion.section
              id="plus-bundle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                SweetPilot Plus Bundle
              </h2>

              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                The SweetPilot Plus bundle gives you access to our complete prompt library with over 10,000 prompts. Here's what's included:
              </p>

              <div className={`p-5 rounded-xl border mb-6 ${isDark ? 'bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/20' : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <Star className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Plus Bundle Features
                    </h3>
                    <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        2,432 prompt cards with up to 5 variations each
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        12 business categories covered
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        Regular updates with new prompts
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        Copy-paste ready templates
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        Works with ChatGPT, Claude, and other AI tools
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Note:</strong> While the platform displays 2,432 prompt cards, each card contains multiple variations, bringing the total to over 10,000 unique prompts.
                </p>
              </div>
            </motion.section>

            {/* Best Practices Section */}
            <motion.section
              id="best-practices"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Best Practices for Using AI Prompts
              </h2>

              <div className="space-y-4 mb-6">
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <Lightbulb className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Be Specific with Variables</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Replace placeholder variables with detailed, specific information. Instead of "[PRODUCT]", write "Our SaaS project management tool for remote teams priced at $29/month".
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <BookOpen className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Iterate and Refine</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Start with a prompt, review the output, and ask the AI to refine specific sections. This iterative approach produces better results than expecting perfection on the first try.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <Search className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Try Multiple Variations</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Each prompt card includes multiple variations. Test different approaches to find what resonates best with your audience and goals.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                      <MessageSquare className={`w-5 h-5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Add Context</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        Provide relevant background information before the prompt. Share your brand voice, target audience, or industry context for more tailored outputs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Migration Section */}
            <motion.section
              id="migration"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Migration to AI Employees
              </h2>

              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                As SweetPilot Prompts approach their end-of-life date, we've integrated and enhanced this functionality in our{' '}
                <Link
                  to="/dashboard/employees"
                  className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  AI Employees
                </Link>
                {' '}platform. Here's what makes AI Employees even better:
              </p>

              <ul className={`space-y-2 mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span><strong>Built-in prompts:</strong> AI Employees come pre-trained with optimized prompts for their specific roles</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span><strong>Conversational interface:</strong> Chat naturally instead of copy-pasting prompts</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span><strong>Context memory:</strong> AI Employees remember your preferences and past interactions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${isDark ? 'bg-green-400' : 'bg-green-600'}`}></span>
                  <span><strong>Integration support:</strong> Connect with your tools for automated workflows</span>
                </li>
              </ul>

              <div className={`p-4 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Ready to upgrade?</strong> Contact{' '}
                  <a
                    href="mailto:help@sweetpilot.com"
                    className={`underline ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-700 hover:text-green-800'}`}
                  >
                    help@sweetpilot.com
                  </a>
                  {' '}and we'll help you transition to AI Employees with your favorite prompts already configured.
                </p>
              </div>
            </motion.section>

            {/* FAQ Section */}
            <motion.section
              id="faq"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-10"
            >
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
                  >
                    <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {faq.question}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Divider */}
            <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="mb-10"
            >
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Related Articles
              </h2>
              <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                {relatedArticles.map((article, index) => (
                  <Link
                    key={article.title}
                    to={article.href}
                    className={`flex items-center justify-between p-4 transition-colors ${
                      index !== relatedArticles.length - 1
                        ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                        : ''
                    } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                  >
                    <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                    <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                  </Link>
                ))}
              </div>
            </motion.section>

            {/* Feedback */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
            >
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                  😞
                </button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                  😐
                </button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                  😃
                </button>
              </div>
            </motion.div>
          </article>

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0" aria-label="Table of contents">
            <div className="sticky top-24">
              <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                      activeSection === section.id
                        ? isDark
                          ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                          : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                        : isDark
                          ? 'text-gray-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        </div>
      </StaticPageLayout>
    </>
  );
}
